import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import MuiButton from '@mui/material/Button';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LandingLayout from './components/LandingLayout';
import Section from './components/Section';
import ALL_BLOG_POSTS from './data/blog-posts';

export default function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const post = ALL_BLOG_POSTS.find((p) => p.slug === slug);

  if (!post) {
    return (
      <LandingLayout>
        <Section background="#F7F7F7" py={{ xs: 8, md: 12 }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              sx={{
                fontFamily: 'Montserrat, sans-serif',
                fontSize: '1.5rem',
                fontWeight: 700,
                color: '#2C2C2C',
                mb: 2,
              }}
            >
              Post not found
            </Typography>
            <MuiButton
              onClick={() => navigate('/blog')}
              sx={{
                fontFamily: 'Montserrat, sans-serif',
                fontWeight: 700,
                color: '#2C2C2C',
                textTransform: 'none',
              }}
            >
              &larr; Back to blog
            </MuiButton>
          </Box>
        </Section>
      </LandingLayout>
    );
  }

  // Simple markdown-to-JSX renderer for our blog content
  const renderContent = (markdown: string) => {
    const lines = markdown.split('\n');
    const elements: React.ReactNode[] = [];
    let listItems: string[] = [];
    let listType: 'ul' | 'ol' | null = null;

    const flushList = () => {
      if (listItems.length > 0 && listType) {
        const Tag = listType === 'ol' ? 'ol' : 'ul';
        elements.push(
          <Box
            component={Tag}
            key={`list-${elements.length}`}
            sx={{
              fontFamily: 'Lato, sans-serif',
              fontSize: '1.05rem',
              color: '#444',
              lineHeight: 1.8,
              pl: 3,
              mb: 2,
              '& li': { mb: 0.75 },
            }}
          >
            {listItems.map((item, j) => (
              <li key={j}>{item}</li>
            ))}
          </Box>
        );
        listItems = [];
        listType = null;
      }
    };

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];

      // Headings
      if (line.startsWith('## ')) {
        flushList();
        elements.push(
          <Typography
            key={i}
            variant="h2"
            sx={{
              fontFamily: 'Montserrat, sans-serif',
              fontSize: { xs: '1.3rem', md: '1.6rem' },
              fontWeight: 700,
              color: '#2C2C2C',
              mt: 4,
              mb: 2,
              lineHeight: 1.3,
            }}
          >
            {line.replace('## ', '')}
          </Typography>
        );
      } else if (line.startsWith('### ')) {
        flushList();
        elements.push(
          <Typography
            key={i}
            variant="h3"
            sx={{
              fontFamily: 'Montserrat, sans-serif',
              fontSize: { xs: '1.1rem', md: '1.25rem' },
              fontWeight: 700,
              color: '#2C2C2C',
              mt: 3,
              mb: 1.5,
              lineHeight: 1.3,
            }}
          >
            {line.replace('### ', '')}
          </Typography>
        );
      }
      // Unordered list
      else if (line.match(/^[-*] /)) {
        if (listType !== 'ul') {
          flushList();
          listType = 'ul';
        }
        listItems.push(line.replace(/^[-*] /, ''));
      }
      // Ordered list
      else if (line.match(/^\d+\. /)) {
        if (listType !== 'ol') {
          flushList();
          listType = 'ol';
        }
        listItems.push(line.replace(/^\d+\. /, ''));
      }
      // Blank line
      else if (line.trim() === '') {
        flushList();
      }
      // Embed graphic: ::embed{src="/foo.html" height=700}
      else if (line.trim().startsWith('::embed')) {
        flushList();
        const src = line.match(/src="([^"]+)"/)?.[1] ?? '';
        const height = parseInt(line.match(/height=(\d+)/)?.[1] ?? '720', 10);
        if (src) {
          elements.push(
            <Box key={i} sx={{ my: 4, width: '100%' }}>
              <Box
                component="iframe"
                src={src}
                title="Embedded graphic"
                sx={{
                  width: '100%',
                  height: `${height}px`,
                  border: '1px solid #E0E0E0',
                  borderRadius: 1,
                  display: 'block',
                }}
                scrolling="no"
              />
            </Box>
          );
        }
      }
      // Bold paragraph or regular paragraph
      else if (line.trim()) {
        flushList();
        // Handle inline bold **text**
        const parts = line.split(/(\*\*.*?\*\*)/g);
        elements.push(
          <Typography
            key={i}
            sx={{
              fontFamily: 'Lato, sans-serif',
              fontSize: '1.05rem',
              color: '#444',
              lineHeight: 1.8,
              mb: 2,
            }}
          >
            {parts.map((part, j) => {
              if (part.startsWith('**') && part.endsWith('**')) {
                return (
                  <Box component="strong" key={j} sx={{ fontWeight: 700, color: '#2C2C2C' }}>
                    {part.slice(2, -2)}
                  </Box>
                );
              }
              return part;
            })}
          </Typography>
        );
      }
    }
    flushList();
    return elements;
  };

  return (
    <LandingLayout>
      <Section background="#FFFFFF" py={{ xs: 6, md: 10 }} maxWidth={780}>
        {/* Back button */}
        <MuiButton
          onClick={() => navigate('/blog')}
          startIcon={<ArrowBackIcon />}
          sx={{
            fontFamily: 'Montserrat, sans-serif',
            fontWeight: 600,
            fontSize: '0.85rem',
            color: '#2C2C2C',
            textTransform: 'none',
            mb: 4,
            '&:hover': { backgroundColor: 'transparent' },
          }}
        >
          All posts
        </MuiButton>

        {/* Meta */}
        <Typography
          sx={{
            fontFamily: 'Lato, sans-serif',
            fontSize: '0.85rem',
            color: '#888',
            mb: 2,
          }}
        >
          {post.author}
        </Typography>

        {/* Title */}
        <Typography
          variant="h1"
          sx={{
            fontFamily: 'Montserrat, sans-serif',
            fontSize: { xs: '1.75rem', md: '2.5rem' },
            fontWeight: 800,
            color: '#2C2C2C',
            lineHeight: 1.2,
            mb: 4,
            pb: 4,
            borderBottom: '2px solid #E7E7E7',
          }}
        >
          {post.title}
        </Typography>

        {/* Body */}
        <Box>{renderContent(post.content)}</Box>

        {/* Footer nav */}
        <Box
          sx={{
            mt: 6,
            pt: 4,
            borderTop: '2px solid #E7E7E7',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <MuiButton
            onClick={() => navigate('/blog')}
            sx={{
              fontFamily: 'Montserrat, sans-serif',
              fontWeight: 700,
              fontSize: '0.9rem',
              color: '#2C2C2C',
              textTransform: 'none',
              border: '2px solid #1e1e1e',
              borderRadius: '6px',
              boxShadow: '3px 3px 0px #1e1e1e',
              px: 4,
              py: 1,
              '&:hover': {
                boxShadow: '1px 1px 0px #1e1e1e',
                transform: 'translate(2px, 2px)',
                backgroundColor: '#F7F7F7',
              },
              transition: 'all 100ms ease-in-out',
            }}
          >
            &larr; Back to all posts
          </MuiButton>
        </Box>
      </Section>
    </LandingLayout>
  );
}
